import { defineComponent as _defineComponent } from 'vue';
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withModifiers as _withModifiers, Fragment as _Fragment } from "vue";
const _hoisted_1 = { class: "relative aspect-[20/19] w-full overflow-hidden rounded-xl bg-gradient-to-b from-ewhite-100 to-ewhite-200/80 ring-1 ring-black/[0.05]" };
const _hoisted_2 = ["src", "alt"];
const _hoisted_3 = {
    key: 1,
    class: "flex h-full w-full flex-col items-center justify-center gap-2 text-eblack-300 transition-transform duration-300 group-hover:scale-105"
};
const _hoisted_4 = { class: "flex h-12 w-12 items-center justify-center rounded-2xl bg-white/80 shadow-sm ring-1 ring-black/[0.04] backdrop-blur-sm" };
const _hoisted_5 = { class: "text-[11px] font-medium tracking-wider uppercase text-eblack-400" };
const _hoisted_6 = { class: "absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full border border-white/40 bg-white/85 px-2.5 py-1 text-xs font-semibold text-eblack-800 shadow-[0_2px_8px_rgba(0,0,0,0.08)] ring-1 ring-black/[0.04] backdrop-blur-md" };
const _hoisted_7 = ["disabled", "title", "aria-label"];
const _hoisted_8 = { class: "flex flex-col px-0.5 pt-3" };
const _hoisted_9 = { class: "flex items-start justify-between gap-3" };
const _hoisted_10 = { class: "line-clamp-2 text-[15px] font-semibold leading-5 tracking-tight text-eblack-900 transition-colors group-hover:text-eblack-950" };
const _hoisted_11 = {
    key: 0,
    class: "flex h-5 flex-shrink-0 items-center gap-1 text-[13px] font-semibold text-eblack-900"
};
const _hoisted_12 = { class: "font-normal text-eblack-400" };
const _hoisted_13 = {
    key: 1,
    class: "flex h-5 flex-shrink-0 items-center gap-1 text-xs font-medium text-eblack-500"
};
const _hoisted_14 = {
    key: 0,
    class: "mt-1 line-clamp-1 text-[13px] font-medium text-eblack-700"
};
const _hoisted_15 = { class: "mt-0.5 line-clamp-1 text-[13px] text-eblack-500" };
const _hoisted_16 = { class: "mt-0.5 text-[13px] text-eblack-500" };
const _hoisted_17 = { class: "mt-3.5 flex items-center justify-between border-t border-ewhite-200/80 pt-3" };
const _hoisted_18 = { class: "flex flex-col" };
const _hoisted_19 = {
    key: 0,
    class: "text-[13px]"
};
const _hoisted_20 = { class: "font-bold text-eblack-900" };
const _hoisted_21 = { class: "text-xs text-eblack-500" };
const _hoisted_22 = {
    key: 1,
    class: "text-xs font-medium text-eblack-400"
};
const _hoisted_23 = { class: "flex items-center gap-1" };
const _hoisted_24 = ["title", "aria-label"];
const _hoisted_25 = ["title", "aria-label"];
const _hoisted_26 = ["disabled", "title", "aria-label"];
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useAbility, useAuthStore } from "@/domains/auth";
import { useConversationCreation } from "@/domains/conversation";
import { getCountryName } from "@/utils/country-name";
import { normalizeServiceMediaItems } from "@domains/service/models/service-media";
import { IconCalendarEvent, IconEdit, IconMessageCircle, IconPhoto, IconTrash, } from "@tabler/icons-vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ServiceCard',
    props: {
        service: {},
        setService: { type: Function },
        onDelete: { type: Function },
        onViewSchedule: { type: Function },
        commissionRange: {},
        priceRange: {}
    },
    setup(__props) {
        const props = __props;
        const { can } = useAbility();
        const authStore = useAuthStore();
        const { t } = useI18n();
        const { findOrRedirect, isCreating: isCreatingConversation } = useConversationCreation();
        const isDeleting = ref(false);
        const showEdit = computed(() => can("manage", "all") || can("manage", "Service"));
        const showDelete = computed(() => can("manage", "all") || authStore.isSuperAdmin);
        const showChat = computed(() => authStore.currentOrgType === "concierge" &&
            can("create", "Conversation") &&
            props.service.id != null);
        const isActive = computed(() => !props.service.state || props.service.state === "ACT");
        const coverPhotoFailed = ref(false);
        const coverPhoto = computed(() => {
            const item = normalizeServiceMediaItems(props.service).find((media) => media.photoUrl);
            return item?.photoUrl ?? null;
        });
        // Reset the broken-image flag when the service (and thus its photo) changes.
        watch(coverPhoto, () => {
            coverPhotoFailed.value = false;
        });
        const locationLabel = computed(() => {
            const parts = [
                getCountryName(props.service.country),
                props.service.location_one,
                props.service.location_two,
            ].filter((part) => part != null && String(part).trim() !== "");
            return parts.length > 0 ? parts.join(", ") : t("services.auto.sin_ubicacion");
        });
        const durationLabel = computed(() => {
            const duration = props.service.duration;
            if (!duration)
                return t("services.auto.duracion_no_especificada", "Duración no especificada");
            const hours = getDurationInHours(duration);
            const type = parseDurationType(duration);
            return `${hours} ${type}`;
        });
        const commissionLabel = computed(() => {
            return formatCommission();
        });
        const providerOrCategoryLabel = computed(() => {
            return (props.service.provider?.name ||
                props.service.category?.name ||
                null);
        });
        function formatCommission() {
            if (props.commissionRange !== undefined)
                return props.commissionRange;
            const provider = props.service.provider;
            if (!provider)
                return "N/A";
            const amount = provider.commission_amount || 0;
            const type = provider.commission_type || "PERCENTAGE";
            if (type === "PERCENTAGE")
                return `${amount}%`;
            if (type === "FIXED")
                return `$${amount}`;
            return "N/A";
        }
        function getDurationInHours(durationMinutes) {
            const hours = (durationMinutes || 0) / 60;
            return hours % 1 === 0 ? hours : parseFloat(hours.toFixed(2));
        }
        function parseDurationType(durationMinutes) {
            const hours = (durationMinutes || 0) / 60;
            return hours > 1 ? t("services.auto.hours") : t("services.auto.hour");
        }
        function handleEditService() {
            props.setService(props.service);
        }
        function handleDetails() {
            props.setService(props.service, true);
        }
        function handleViewSchedule() {
            props.onViewSchedule?.(props.service);
        }
        async function handleDelete() {
            if (!props.service.id || !props.onDelete)
                return;
            if (!window.confirm(t("services.auto.confirm_delete_service", { name: props.service.name }))) {
                return;
            }
            isDeleting.value = true;
            try {
                await props.onDelete(props.service.id);
            }
            finally {
                isDeleting.value = false;
            }
        }
        async function handleCreateChat() {
            if (props.service.id == null)
                return;
            await findOrRedirect({
                serviceId: String(props.service.id),
                providerId: props.service.provider_id != null ? String(props.service.provider_id) : undefined,
            });
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                class: "group relative flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-ewhite-200 bg-white p-3 shadow-[0_1px_3px_rgba(0,0,0,0.03),0_6px_16px_rgba(0,0,0,0.02)] transition-all duration-300 ease-out hover:-translate-y-1 hover:border-ewhite-300 hover:shadow-[0_12px_28px_rgba(0,0,0,0.08),0_4px_12px_rgba(0,0,0,0.03)]",
                onClick: _cache[6] || (_cache[6] = ($event) => (handleDetails()))
            }, [
                _createElementVNode("div", _hoisted_1, [
                    (coverPhoto.value && !coverPhotoFailed.value)
                        ? (_openBlock(), _createElementBlock("img", {
                            key: 0,
                            src: coverPhoto.value,
                            alt: __props.service.name,
                            class: "h-full w-full object-cover transition duration-500 ease-out group-hover:scale-[1.04]",
                            loading: "lazy",
                            onError: _cache[0] || (_cache[0] = ($event) => (coverPhotoFailed.value = true))
                        }, null, 40, _hoisted_2))
                        : (_openBlock(), _createElementBlock("div", _hoisted_3, [
                            _createElementVNode("div", _hoisted_4, [
                                _createVNode(_unref(IconPhoto), {
                                    class: "h-6 w-6 text-eblack-400",
                                    stroke: "{1.5}"
                                })
                            ]),
                            _createElementVNode("span", _hoisted_5, _toDisplayString(_ctx.$t("services.auto.sin_imagen", "Sin imagen")), 1)
                        ])),
                    _createElementVNode("span", _hoisted_6, [
                        _createElementVNode("span", {
                            class: _normalizeClass(["h-1.5 w-1.5 rounded-full", isActive.value ? 'bg-egreen-500' : 'bg-eblack-300'])
                        }, null, 2),
                        _createTextVNode(" " + _toDisplayString(isActive.value ? _ctx.$t("services.auto.activo") : _ctx.$t("services.auto.inactivo")), 1)
                    ]),
                    (showChat.value)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 2,
                            type: "button",
                            class: "absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full border border-white/40 bg-white/85 text-eblack-800 shadow-[0_2px_8px_rgba(0,0,0,0.08)] ring-1 ring-black/[0.04] backdrop-blur-md transition-all duration-200 hover:scale-105 hover:bg-white hover:text-eblack-950 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60",
                            disabled: _unref(isCreatingConversation),
                            title: _ctx.$t('services.auto.chat'),
                            "aria-label": _ctx.$t('services.auto.chat'),
                            onClick: _cache[1] || (_cache[1] = _withModifiers(($event) => (handleCreateChat()), ["stop"]))
                        }, [
                            _createVNode(_unref(IconMessageCircle), {
                                class: "h-4 w-4",
                                stroke: "{1.75}"
                            })
                        ], 8, _hoisted_7))
                        : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_8, [
                    _createElementVNode("div", _hoisted_9, [
                        _createElementVNode("h3", _hoisted_10, _toDisplayString(__props.service.name), 1),
                        (__props.service.reviewCount && __props.service.reviewCount > 0)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                _cache[7] || (_cache[7] = _createElementVNode("svg", {
                                    xmlns: "http://www.w3.org/2000/svg",
                                    class: "h-3.5 w-3.5 flex-shrink-0 fill-eblack-900 text-eblack-900",
                                    viewBox: "0 0 24 24",
                                    "aria-hidden": "true"
                                }, [
                                    _createElementVNode("path", { d: "M12 17.75l-6.172 3.245l1.179 -6.873l-5 -4.867l6.9 -1l3.086 -6.253l3.086 6.253l6.9 1l-5 4.867l1.179 6.873z" })
                                ], -1)),
                                _createElementVNode("span", null, _toDisplayString(__props.service.averageRating?.toFixed(1)), 1),
                                _createElementVNode("span", _hoisted_12, "(" + _toDisplayString(__props.service.reviewCount) + ")", 1)
                            ]))
                            : (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                _cache[8] || (_cache[8] = _createElementVNode("svg", {
                                    xmlns: "http://www.w3.org/2000/svg",
                                    class: "h-3.5 w-3.5 flex-shrink-0 fill-amber-400 text-amber-400",
                                    viewBox: "0 0 24 24",
                                    "aria-hidden": "true"
                                }, [
                                    _createElementVNode("path", { d: "M12 17.75l-6.172 3.245l1.179 -6.873l-5 -4.867l6.9 -1l3.086 -6.253l3.086 6.253l6.9 1l-5 4.867l1.179 6.873z" })
                                ], -1)),
                                _createElementVNode("span", null, _toDisplayString(_ctx.$t("reviews.catalog.no_reviews", "Sin reseñas aún")), 1)
                            ]))
                    ]),
                    (providerOrCategoryLabel.value)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_14, _toDisplayString(providerOrCategoryLabel.value), 1))
                        : _createCommentVNode("", true),
                    _createElementVNode("p", _hoisted_15, _toDisplayString(locationLabel.value), 1),
                    _createElementVNode("p", _hoisted_16, [
                        _createElementVNode("span", null, _toDisplayString(durationLabel.value), 1),
                        (commissionLabel.value && commissionLabel.value !== 'N/A')
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                _cache[9] || (_cache[9] = _createElementVNode("span", { class: "mx-1.5 text-eblack-300" }, "·", -1)),
                                _createElementVNode("span", null, _toDisplayString(commissionLabel.value) + " " + _toDisplayString(_ctx.$t("services.auto.comision_label")), 1)
                            ], 64))
                            : _createCommentVNode("", true)
                    ]),
                    _createElementVNode("div", _hoisted_17, [
                        _createElementVNode("div", _hoisted_18, [
                            (__props.priceRange)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_19, [
                                    _createElementVNode("span", _hoisted_20, _toDisplayString(__props.priceRange.amount), 1),
                                    _createElementVNode("span", _hoisted_21, _toDisplayString(__props.priceRange.priceType === "HOUR"
                                        ? ` ${_ctx.$t("services.auto.por_hora")}`
                                        : ` ${_ctx.$t("services.auto.por_persona")}`), 1)
                                ]))
                                : (_openBlock(), _createElementBlock("span", _hoisted_22, _toDisplayString(_ctx.$t("services.auto.disponibilidad_sin_configurar")), 1))
                        ]),
                        _createElementVNode("div", _hoisted_23, [
                            (__props.service.id != null)
                                ? (_openBlock(), _createElementBlock("button", {
                                    key: 0,
                                    type: "button",
                                    class: "inline-flex h-8 w-8 items-center justify-center rounded-full text-eblack-500 transition hover:bg-ewhite-100 hover:text-eblack-900 active:scale-95",
                                    title: _ctx.$t('services.auto.ver_horarios'),
                                    "aria-label": _ctx.$t('services.auto.ver_horarios'),
                                    onClick: _cache[2] || (_cache[2] = _withModifiers(($event) => (handleViewSchedule()), ["stop"]))
                                }, [
                                    _createVNode(_unref(IconCalendarEvent), {
                                        class: "h-4 w-4",
                                        stroke: "{1.75}"
                                    })
                                ], 8, _hoisted_24))
                                : _createCommentVNode("", true),
                            (showEdit.value)
                                ? (_openBlock(), _createElementBlock("button", {
                                    key: 1,
                                    type: "button",
                                    class: "inline-flex h-8 w-8 items-center justify-center rounded-full text-eblack-500 transition hover:bg-ewhite-100 hover:text-eblack-900 active:scale-95",
                                    title: _ctx.$t('services.auto.editar'),
                                    "aria-label": _ctx.$t('services.auto.editar'),
                                    onClick: _cache[3] || (_cache[3] = _withModifiers(($event) => (handleEditService()), ["stop"]))
                                }, [
                                    _createVNode(_unref(IconEdit), {
                                        class: "h-4 w-4",
                                        stroke: "{1.75}"
                                    })
                                ], 8, _hoisted_25))
                                : _createCommentVNode("", true),
                            (showDelete.value && __props.onDelete)
                                ? (_openBlock(), _createElementBlock("button", {
                                    key: 2,
                                    "data-testid": "delete-service-btn",
                                    type: "button",
                                    class: "inline-flex h-8 w-8 items-center justify-center rounded-full text-red-400 transition hover:bg-red-50 hover:text-red-600 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50",
                                    disabled: isDeleting.value,
                                    title: _ctx.$t('common.actions.delete'),
                                    "aria-label": _ctx.$t('common.actions.delete'),
                                    onClick: _cache[4] || (_cache[4] = _withModifiers(($event) => (handleDelete()), ["stop"]))
                                }, [
                                    _createVNode(_unref(IconTrash), {
                                        class: "h-4 w-4",
                                        stroke: "{1.75}"
                                    })
                                ], 8, _hoisted_26))
                                : _createCommentVNode("", true),
                            _createElementVNode("button", {
                                type: "button",
                                class: "group/btn ml-1 inline-flex items-center gap-1.5 rounded-full bg-eblack-900 px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm transition-all duration-200 hover:bg-black hover:shadow-md active:scale-95",
                                onClick: _cache[5] || (_cache[5] = _withModifiers(($event) => (handleDetails()), ["stop"]))
                            }, [
                                _createElementVNode("span", null, _toDisplayString(_ctx.$t("services.auto.ver_detalle")), 1),
                                _cache[10] || (_cache[10] = _createElementVNode("svg", {
                                    xmlns: "http://www.w3.org/2000/svg",
                                    class: "h-3.5 w-3.5 transition-transform duration-200 group-hover/btn:translate-x-0.5",
                                    viewBox: "0 0 24 24",
                                    fill: "none",
                                    stroke: "currentColor",
                                    "stroke-width": "2.2",
                                    "stroke-linecap": "round",
                                    "stroke-linejoin": "round",
                                    "aria-hidden": "true"
                                }, [
                                    _createElementVNode("path", { d: "M5 12l14 0" }),
                                    _createElementVNode("path", { d: "M13 18l6 -6" }),
                                    _createElementVNode("path", { d: "M13 6l6 6" })
                                ], -1))
                            ])
                        ])
                    ])
                ])
            ]));
        };
    }
});
